import { Checkbox, Chips, FormField, type IChipsOption, InputAdornment, NumberInput, Select } from '@cian/ui-kit';

import { type SyntheticEvent, memo, useCallback, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import {
  apartmentsWithDecorationChange,
  deadlineChange,
  maxPriceChange,
  minPriceChange,
  readyApartmentsChange,
  regionChange,
  roomsChange,
  seriaPlusChange,
} from 'shared/actions/filter/actions';
import { DEFAULT_DEADLINE_VALUE } from 'shared/reducers/filter/helpers';
import { getDeadline, getDeadlineVariantsForSelect } from 'shared/selectors/filters/deadline';
import { getApartmentsWithDecoration } from 'shared/selectors/filters/getApartmentsWithDecoration';
import { getFilterLocation } from 'shared/selectors/filters/getFilterLocation';
import { getReadyApartments } from 'shared/selectors/filters/getReadyApartments';
import { getRooms } from 'shared/selectors/filters/getRooms';
import { getMaxPrice, getMinPrice } from 'shared/selectors/filters/prices';
import { seriaPlusSelector } from 'shared/selectors/filters/seriaPlusSelector';
import { getLocationsForSelect } from 'shared/selectors/locations/getLocationsForSelect';
import { ERoom } from 'shared/types/filter';
import { type TThunkDispatch } from 'shared/types/redux/thunk';
import { addQueryParameter, setQueryParameter } from 'shared/utils/url';

import styles from './Filters.css';

const roomsOptions: IChipsOption<ERoom>[] = [
  { label: 'Студия', value: ERoom.Studio },
  { label: '1', value: ERoom.OneRoom },
  { label: '2', value: ERoom.TwoRoom },
  { label: '3+', value: ERoom.ThreeRoom },
];

const SIZE = 'M';

export const Filters = memo(function Filters() {
  const dispatch: TThunkDispatch = useDispatch();
  const regions = useSelector(getLocationsForSelect, shallowEqual);
  const selectedRegion = useSelector(getFilterLocation);

  const deadlineOptions = useSelector(getDeadlineVariantsForSelect);
  const deadline = useSelector(getDeadline);

  const seriaPlus = useSelector(seriaPlusSelector);

  const [error, setError] = useState(false);
  const minPrice = useSelector(getMinPrice);
  const maxPrice = useSelector(getMaxPrice);

  const rooms = useSelector(getRooms);

  const readyApartments = useSelector(getReadyApartments);

  const apartmentsWithDecoration = useSelector(getApartmentsWithDecoration);

  const handleApartmentsWithDecorationChange = useCallback(
    (_event: SyntheticEvent, value: boolean) => {
      dispatch(apartmentsWithDecorationChange(value));
      setQueryParameter('apartmentsWithDecoration', value);
    },
    [dispatch],
  );

  const handleReadyApartmentsChange = useCallback(
    (_event: SyntheticEvent, value: boolean) => {
      dispatch(readyApartmentsChange(value));
      setQueryParameter('readyApartments', value);
    },
    [dispatch],
  );

  const handleRoomsChange = useCallback(
    (value: ERoom[]) => {
      dispatch(roomsChange(value));
      setQueryParameter('rooms', value.join(','));
    },
    [dispatch],
  );

  const handlePriceChange =
    (type: 'minPrice' | 'maxPrice') =>
    (value: number | undefined): void => {
      const handler = {
        maxPrice: maxPriceChange,
        minPrice: minPriceChange,
      };

      dispatch(handler[type](value));
      setQueryParameter(type, value);
    };

  const handleDeadlineChange = useCallback(
    (value: number) => {
      dispatch(deadlineChange(value));
      setQueryParameter('deadline', value);
    },
    [dispatch],
  );

  const handleRegionChange = useCallback(
    (regionId: number) => {
      dispatch(regionChange(regionId));
      addQueryParameter('location', String(regionId));
    },
    [dispatch],
  );

  const handlePriceBlur = useCallback(() => {
    if (minPrice != null && maxPrice != null) {
      setError(minPrice > maxPrice);
    }
  }, [maxPrice, minPrice]);

  const handleSeriaPlusChange = useCallback(
    (_event: SyntheticEvent, value: boolean) => {
      dispatch(seriaPlusChange(value));
      setQueryParameter('seriaPlus', value);
    },
    [dispatch],
  );

  return (
    <div className={styles['container']} data-testid="pik-projects-filters">
      <div className={styles['row']}>
        <FormField dataTestId="form-field-region" label="Регион">
          <Select options={regions} size={SIZE} value={selectedRegion} width={263} onChange={handleRegionChange} />
        </FormField>

        <FormField dataTestId="form-field-rooms" label="Комнатность">
          <Chips<ERoom>
            options={roomsOptions}
            selectType="multiple"
            size={SIZE}
            value={rooms}
            onChange={handleRoomsChange}
          />
        </FormField>

        <FormField dataTestId="form-field-price" label="Цена">
          <div className={styles['price-range-inputs']}>
            <NumberInput
              data-testid="min-price"
              invalid={error}
              min={0}
              placeholder="от"
              rightAdornment={<InputAdornment>₽</InputAdornment>}
              size={SIZE}
              value={minPrice}
              width={155}
              onBlur={handlePriceBlur}
              onChange={handlePriceChange('minPrice')}
            />

            <NumberInput
              data-testid="max-price"
              invalid={error}
              min={0}
              placeholder="до"
              rightAdornment={<InputAdornment>₽</InputAdornment>}
              size={SIZE}
              value={maxPrice}
              width={155}
              onBlur={handlePriceBlur}
              onChange={handlePriceChange('maxPrice')}
            />
          </div>
        </FormField>

        <FormField dataTestId="form-field-deadline" label="Срок сдачи">
          <Select
            options={deadlineOptions}
            size={SIZE}
            value={deadline || DEFAULT_DEADLINE_VALUE}
            width={170}
            onChange={handleDeadlineChange}
          />
        </FormField>
      </div>

      <div className={styles['row']}>
        <FormField dataTestId="form-field-ready-apartments">
          <Checkbox checked={readyApartments} label="Готовые квартиры" onChange={handleReadyApartmentsChange} />
        </FormField>

        <FormField dataTestId="form-field-apartments-with-decoration">
          <Checkbox
            checked={apartmentsWithDecoration}
            label="С отделкой"
            onChange={handleApartmentsWithDecorationChange}
          />
        </FormField>

        <FormField dataTestId="form-field-seria-plus">
          <Checkbox checked={seriaPlus} label="Серия плюс" onChange={handleSeriaPlusChange} />
        </FormField>
      </div>
    </div>
  );
});
